/**
 * Shared Day / Working / Start / End editor for provider weekly availability.
 */
import { ActionIcon, Box, Select, Switch, Text, Tooltip } from '@mantine/core';
import { IconCopy } from '@tabler/icons-react';
import {
  DEFAULT_PROVIDER_AVAILABILITY_SLOT,
  PROVIDER_AVAILABILITY_UI_DAYS,
  type ProviderAvailabilityUiDay,
  type ProviderWeeklyAvailability,
} from './providerAvailabilityTypes';
import {
  isInvalidSlotTimeRange,
  TIME_SLOT_OPTIONS,
  timeSlotOptionsInWindow,
  dayWindowHasSelectableRange,
} from './providerAvailabilityValidation';

const BORDER_COLOR = 'var(--mantine-color-gray-5)';
const GRID_COLUMNS = '36px 44px 110px 20px 110px 36px';

export interface WeeklyAvailabilityEditorProps {
  value: ProviderWeeklyAvailability;
  onChange: (next: ProviderWeeklyAvailability) => void;
  loading?: boolean;
  /** When set, start/end options are limited to each day's window and closed days cannot be enabled. */
  dayWindows?: Partial<Record<ProviderAvailabilityUiDay, { openingMinutes: number; closingMinutes: number }>>;
}

export function WeeklyAvailabilityEditor({
  value,
  onChange,
  loading = false,
  dayWindows,
}: WeeklyAvailabilityEditorProps): JSX.Element {
  if (loading) {
    return (
      <Text size="sm" c="dimmed">
        Loading availability…
      </Text>
    );
  }

  return (
    <Box>
      <Box
        style={{
          display: 'grid',
          gridTemplateColumns: GRID_COLUMNS,
          columnGap: 12,
          alignItems: 'end',
          paddingBottom: 4,
          borderBottom: `1px solid ${BORDER_COLOR}`,
          marginBottom: 12,
        }}
      >
        <Text size="xs" c="dimmed" fw={600}>
          Day
        </Text>
        <Text size="xs" c="dimmed" fw={600}>
          Working
        </Text>
        <Text size="xs" c="dimmed" fw={600}>
          Start
        </Text>
        <span aria-hidden />
        <Text size="xs" c="dimmed" fw={600}>
          End
        </Text>
        <span aria-hidden />
      </Box>
      {PROVIDER_AVAILABILITY_UI_DAYS.map((day) => {
        const dayData = value[day];
        const enabled = dayData?.enabled ?? false;
        const slot = dayData?.slots?.[0] ?? { start: '', end: '' };
        const invalidTimeRange = isInvalidSlotTimeRange(slot);
        const window = dayWindows?.[day];
        const selectable = Boolean(
          window && dayWindowHasSelectableRange(window.openingMinutes, window.closingMinutes),
        );
        const closedByParent = Boolean(dayWindows) && !selectable;
        const startOptions = selectable && window
          ? timeSlotOptionsInWindow(window.openingMinutes, window.closingMinutes, 'start')
          : dayWindows
            ? []
            : TIME_SLOT_OPTIONS;
        const endOptions = selectable && window
          ? timeSlotOptionsInWindow(window.openingMinutes, window.closingMinutes, 'end')
          : dayWindows
            ? []
            : TIME_SLOT_OPTIONS;
        const showHours = enabled && !closedByParent;
        return (
          <Box
            key={day}
            style={{
              display: 'grid',
              gridTemplateColumns: GRID_COLUMNS,
              columnGap: 12,
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <Text size="sm" fw={500}>
              {day}
            </Text>
            <Switch
              size="sm"
              checked={showHours}
              disabled={closedByParent}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                const defaultSlot = {
                  start: startOptions[0]?.value ?? DEFAULT_PROVIDER_AVAILABILITY_SLOT.start,
                  end: endOptions[endOptions.length - 1]?.value ?? DEFAULT_PROVIDER_AVAILABILITY_SLOT.end,
                };
                onChange({
                  ...value,
                  [day]: on
                    ? {
                        enabled: true,
                        slots: dayData?.slots?.length
                          ? [{ ...dayData.slots[0] }]
                          : [defaultSlot],
                      }
                    : { enabled: false, slots: dayData?.slots ?? [] },
                });
              }}
              aria-label={`${day} working`}
            />
            {showHours ? (
              <>
                <Select
                  size="xs"
                  data={startOptions}
                  value={slot.start || null}
                  placeholder="Start time"
                  onChange={(v) => {
                    onChange({
                      ...value,
                      [day]: {
                        enabled: true,
                        slots: [{ start: v ?? '', end: value[day]?.slots?.[0]?.end ?? '' }],
                      },
                    });
                  }}
                  error={invalidTimeRange}
                />
                <Text size="xs" c="dimmed" ta="center">
                  –
                </Text>
                <Select
                  size="xs"
                  data={endOptions}
                  value={slot.end || null}
                  placeholder="End time"
                  onChange={(v) => {
                    onChange({
                      ...value,
                      [day]: {
                        enabled: true,
                        slots: [{ start: value[day]?.slots?.[0]?.start ?? '', end: v ?? '' }],
                      },
                    });
                  }}
                  error={invalidTimeRange}
                />
                <Tooltip label="Copy hours to other working days">
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    color="gray"
                    onClick={() => {
                      const source = value[day];
                      if (!source?.enabled || !source.slots[0]?.start || !source.slots[0]?.end) {
                        return;
                      }
                      const { start, end } = source.slots[0];
                      const next = { ...value };
                      for (const d of PROVIDER_AVAILABILITY_UI_DAYS) {
                        if (d === day || !next[d]?.enabled) continue;
                        const dest = dayWindows?.[d];
                        if (dayWindows) {
                          if (!dest) continue;
                          const startOk = timeSlotOptionsInWindow(dest.openingMinutes, dest.closingMinutes, 'start')
                            .some((o) => o.value === start);
                          const endOk = timeSlotOptionsInWindow(dest.openingMinutes, dest.closingMinutes, 'end')
                            .some((o) => o.value === end);
                          if (!startOk || !endOk) continue;
                        }
                        next[d] = { enabled: true, slots: [{ start, end }] };
                      }
                      onChange(next);
                    }}
                  >
                    <IconCopy size={14} />
                  </ActionIcon>
                </Tooltip>
              </>
            ) : (
              <>
                <Text size="sm" c="dimmed" style={{ gridColumn: '3 / 6' }}>
                  Not working
                </Text>
                <Box aria-hidden />
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}
